import { debounce, isEqual } from 'lodash';

import type { ChangedResponse } from 'bundles/compound-assessments/components/local-state/changed-response/types';
import type {
  BatchRubricUpdateFunction,
  SubmitGradesFunction,
} from 'bundles/grading/api/BatchUpdateAndSubmitGradesMutationWrapper';
import type {
  BatchUpdateResponseSgaSubmission,
  BatchUpdateReviewPartResponsesMutationVariables,
  GradingStepActionType,
  SubmissionReference,
  SubmitGradesToGradeBookMutationVariables,
} from 'bundles/grading/api/types';
import type {
  AutoSaveTaskState,
  ReviewPartProgress,
} from 'bundles/grading/components/staff-graded-assignments/auto-save/utils';
import {
  canSubmitGrades,
  constructResponseCollections,
  defaultAutoSaveTaskState,
} from 'bundles/grading/components/staff-graded-assignments/auto-save/utils';

type InitialData = {
  submissionReference: SubmissionReference;
  conflictMetadata?: string | null;
  allowedGradingActions: GradingStepActionType[];
  gradingTaskIdHash: string;
};

export const DEBOUNCE_WAIT_TIME = 1500;

/**
 * AutoSaveTask stores required mutation metadata variables (conflictMetadata, gradingTaskIdHash,
 * allowedGradingActions, etc)  * and the latest rubric changes in one instance. It ensures the
 * latest version of mutation metadata variables by updating these upon successful mutation responses.
 * It is responsible for debouncedSave and keeps status of a single AutoSave process in the `state`
 * property.
 */
export class AutoSaveTask {
  private declare latestDataInCache: ChangedResponse[];

  private state: AutoSaveTaskState = { ...defaultAutoSaveTaskState };

  // doesn't change between requests
  declare submissionReference: SubmissionReference;

  // doesn't change between requests
  declare gradingTaskIdHash: string;

  // The most up-to-date value must be sent with mutation.
  declare conflictMetadata?: string | null;

  // The most up-to-date value must be sent with mutation.
  declare allowedGradingActions: GradingStepActionType[];

  /**
   * Takes initial mutation metadata variables
   * @param data InitialData
   */
  constructor(data: InitialData) {
    this.submissionReference = data.submissionReference;
    this.gradingTaskIdHash = data.gradingTaskIdHash;
    this.conflictMetadata = data.conflictMetadata;
    this.allowedGradingActions = data.allowedGradingActions;

    this.latestDataInCache = [];
  }

  debouncedSave = debounce(this.save, DEBOUNCE_WAIT_TIME);

  // update latestDataInCache
  setLatestDataInCache(value: ChangedResponse[]) {
    this.latestDataInCache = value;
    this.state.hasUnsavedChanges = true;
  }

  getLatestDataInCache() {
    return this.latestDataInCache;
  }

  getState() {
    return this.state;
  }

  async save(
    batchRubricUpdate: BatchRubricUpdateFunction,
    submitGrades: SubmitGradesFunction,
    onStateChange: (action?: string) => void,
    reviewPartProgress: ReviewPartProgress
  ) {
    const dataToSave = this.latestDataInCache.slice();

    // If the previous changeSet is still being saved, retry in DEBOUNCE_WAIT_TIME
    if (this.state.isSaving || this.state.isSubmitting) {
      this.debouncedSave(batchRubricUpdate, submitGrades, onStateChange, reviewPartProgress);
      return;
    }

    let submitGradesPayload: SubmitGradesToGradeBookMutationVariables | null = null;
    let saveResult: BatchUpdateResponseSgaSubmission | undefined;

    const saveAndSubmit = reviewPartProgress.areRubricsComplete;
    if (this.state.hasUnsavedChanges) {
      try {
        this.state.saveError = null;
        this.state.submitError = null;
        if (saveAndSubmit) {
          this.state.isSubmitting = true;
        } else {
          this.state.isSaving = true;
        }
        onStateChange();

        const responseCollections = constructResponseCollections(dataToSave);

        const batchUpdatePayload: BatchUpdateReviewPartResponsesMutationVariables = {
          input: {
            allowedGradingActions: this.allowedGradingActions,
            conflictMetadata: this.conflictMetadata,
            gradingTaskIdHash: this.gradingTaskIdHash,
            submissionReference: this.submissionReference,
            isRubricComplete: reviewPartProgress.areRubricsComplete,
            ...responseCollections,
          },
        };

        const batchUpdateResponse = await batchRubricUpdate({ variables: batchUpdatePayload });
        saveResult = batchUpdateResponse?.data?.GroupGrading_BatchUpdateReviewPartResponses.sgaSubmission;

        this.conflictMetadata = saveResult?.gradingStepActionMetadata?.conflictMetadata;
        this.gradingTaskIdHash = saveResult?.gradingStepActionMetadata?.gradingTaskIdHash || '';
        this.allowedGradingActions = saveResult?.gradingStepActionMetadata?.allowedGradingActions || [];
      } catch (err) {
        if (saveAndSubmit) {
          this.state.submitError = err;
        } else {
          this.state.saveError = err;
        }
      } finally {
        this.state.isSaving = false;
        this.state.isSubmitting = false;
        onStateChange();
      }
    }

    // It is possible there were no data changes to save, but we need
    // "forceSubmit" (e.g. when unsubmitted data from Old SGA Grading page).
    const safeToSubmit = canSubmitGrades(reviewPartProgress.areRubricsComplete, this.allowedGradingActions);

    // we allow to "forceSubmit"
    // (e.g. when unsubmitted data from Old SGA Grading page)
    // no auto submit should be called after batchUpdate
    const proceedWithSubmit = !this.state.hasUnsavedChanges && safeToSubmit;
    if (proceedWithSubmit) {
      try {
        this.state.isSubmitting = true;
        this.state.submitError = null;
        onStateChange();

        submitGradesPayload = {
          input: {
            gradingTaskIdHash: this.gradingTaskIdHash,
            conflictMetadata: this.conflictMetadata,
            submissionReference: this.submissionReference,
          },
        };
        const submitGradesResponse = await submitGrades({ variables: submitGradesPayload });
        const submitResult = submitGradesResponse?.data?.GroupGrading_SubmitGradesToGradeBook.sgaSubmission;
        this.conflictMetadata = submitResult?.gradingStepActionMetadata?.conflictMetadata;
        this.gradingTaskIdHash = submitResult?.gradingStepActionMetadata?.gradingTaskIdHash || '';
        this.allowedGradingActions = submitResult?.gradingStepActionMetadata?.allowedGradingActions || [];
      } catch (err) {
        this.state.submitError = err;
      } finally {
        this.state.isSubmitting = false;
        onStateChange();
      }
    }
    if (isEqual(dataToSave, this.latestDataInCache)) {
      this.state.hasUnsavedChanges = false;
      onStateChange();
    }
  }
}
